import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Shared month picker used on Dashboard + Analytics.
 * Props:
 * - value: "YYYY-MM"
 * - onChange: (nextMonth: "YYYY-MM") => void
 * - testId
 */
export default function MonthPicker({ value, onChange, testId = "month-picker" }) {
  const [months, setMonths] = useState([]); // [{month, label, is_current}]
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/analytics/available-months")
      .then(r => setMonths(r.data || []))
      .catch(() => setMonths([]));
  }, []);

  const currentIndex = months.findIndex(m => m.month === value);
  const currentLabel = currentIndex >= 0
    ? months[currentIndex].label
    : (value ? new Date(`${value}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "This Month");
  const isCurrent = currentIndex >= 0 ? months[currentIndex].is_current : false;

  // Newer = smaller index (list is newest first)
  const canGoNewer = currentIndex > 0;
  const canGoOlder = currentIndex >= 0 && currentIndex < months.length - 1;

  const step = (delta) => {
    if (currentIndex < 0) return;
    const target = months[currentIndex + delta];
    if (target) onChange(target.month);
  };

  return (
    <div
      data-testid={testId}
      className="inline-flex items-center rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden"
    >
      <button
        type="button"
        data-testid={`${testId}-prev`}
        onClick={() => step(1)}
        disabled={!canGoOlder}
        aria-label="Previous month"
        className="w-9 h-9 grid place-items-center text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-label`}
            className="flex items-center gap-1.5 px-3 h-9 text-sm font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-x border-slate-200 dark:border-slate-800 whitespace-nowrap"
          >
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            {currentLabel}
            {isCurrent && (
              <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-spidey-red text-white uppercase tracking-wider">Now</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-56 p-1 max-h-72 overflow-y-auto" data-testid={`${testId}-menu`}>
          {months.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">No months available yet.</div>
          ) : (
            months.map(m => (
              <button
                key={m.month}
                type="button"
                data-testid={`${testId}-option-${m.month}`}
                onClick={() => { onChange(m.month); setOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${m.month === value ? "bg-spidey-red text-white" : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
              >
                <span>{m.label}</span>
                {m.is_current && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider ${m.month === value ? "bg-white/25 text-white" : "bg-spidey-red text-white"}`}>Now</span>
                )}
              </button>
            ))
          )}
        </PopoverContent>
      </Popover>

      <button
        type="button"
        data-testid={`${testId}-next`}
        onClick={() => step(-1)}
        disabled={!canGoNewer}
        aria-label="Next month"
        className="w-9 h-9 grid place-items-center text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
